/**
 * landingAuditor.content.js — el copy del auditor SEO público, en cinco idiomas.
 *
 * Esto vivía dentro de `landingV2.content.js`, que eran 3.365 líneas de copy
 * para la landing anterior. Al retirar la V2 se quedó como lo ÚNICO vivo de
 * aquel fichero: 730 líneas de 3.365.
 *
 * Se saca a su propio módulo en vez de dejar el original con el 78 % muerto
 * porque no es contenido que se cargue aparte: `AuditorSeo` se monta dentro de
 * la home (`PuntuacionSeoV3`), así que todo el fichero viajaba en el bundle
 * principal — lo descargaba también quien entra al panel y no ve una landing en
 * su vida.
 *
 * Mismo patrón que el resto del copy público: un objeto por idioma y un
 * selector con fallback a español por idioma ENTERO. Un objeto a medio traducir
 * daría una pantalla mitad en un idioma y mitad en otro, que se lee peor que
 * una entera en español.
 */

const es = {
  kicker: 'Gratis, en 10 segundos',
  title: 'Mira qué ve Google de tu negocio',
  subtitle: 'Busca tu ficha y te decimos cómo está hoy. Sin registrarte y sin conectar nada.',
  search: {
    label: 'Tu negocio',
    placeholder: 'Nombre de tu negocio y ciudad',
    button: 'Analizar gratis',
    minChars: 'Escribe al menos 3 letras',
    searching: 'Buscando…',
    noResults: 'No lo encontramos. Prueba a añadir la ciudad.',
    pickTitle: 'Elige tu negocio',
    again: 'Buscar otro',
  },
  report: {
    loading: 'Analizando tu ficha…',
    scoreTitle: 'Tu ficha ahora mismo',
    outOf: 'sobre 100',
    labels: {
      excellent: 'Excelente',
      good: 'Bien',
      fair: 'Mejorable',
      critical: 'Crítico',
    },
    blocks: {
      reviews_volume: 'Número de reseñas',
      reviews_rating: 'Nota media',
      reviews_pace: 'Última reseña',
      profile: 'Datos de la ficha',
      photos: 'Fotos',
    },
    unknown: 'Google no lo publica',
    today: 'hoy',
    daysAgo: 'hace {n} días',
    reviewsCount: '{n} reseñas',
    noRating: 'todavía sin nota',
    photosCount: '{n} fotos',
    photosCapped: '10 fotos o más',
    noPhotos: 'ninguna',
    profileComplete: 'completos',
    profileMissing: 'falta {n}',
    profileItems: {
      phone: 'teléfono',
      website: 'web',
      hours: 'horario',
      category: 'categoría',
    },
    warnings: {
      closed_temporarily: 'Google te enseña como «cerrado temporalmente».',
      closed_permanently: 'Google te enseña como «cerrado permanentemente».',
    },
    source: 'Datos públicos de Google. No hemos tocado tu ficha.',
    mapsLink: 'Ver en Google Maps',
    checklist: {
      website: 'Web',
      phone: 'Teléfono',
      hours: 'Horario',
      category: 'Categoría',
      photos: '10+ fotos',
      reviews_fresh: 'Reseñas recientes',
    },
    checklistUnknown: 'Sin datos',
    highlights: {
      reviews_low_recent: '{count} de tus {of} últimas reseñas son de 3★ o menos',
      reviews_foreign_language: '{count} de tus {of} últimas reseñas están en {language}',
      hours_days: 'Tienes horario puesto {days} días de la semana',
    },
    languages: {
      en: 'inglés',
      fr: 'francés',
      de: 'alemán',
      it: 'italiano',
      pt: 'portugués',
      es: 'español',
      nl: 'neerlandés',
      ru: 'ruso',
    },
    benchmarkTitle: 'Lo que vemos en las fichas que gestionamos',
    benchmarks: {
      website: 'Las que tienen web reciben un {n} % más de impresiones en Google que las que no.',
      photos: 'Las que tienen 10 o más fotos reciben un {n} % más de impresiones que las que tienen menos.',
    },
    benchmarkSample: 'Medido sobre {n} fichas reales, comparando negocios de tamaño parecido. Es lo que observamos, no lo que te prometemos.',
    points: '{pts} de {max} puntos',
    gapTitle: 'Dónde pierdes los puntos',
    gapLine: 'Te faltan {total} puntos.',
    gapReviews: '{reviews} están en tus reseñas.',
    leversTitle: 'Lo que más te subiría',
    levers: {
      reviews_volume: 'Llegar a {target} reseñas',
      reviews_rating: 'Subir la nota media a {target}',
      reviews_pace: 'Conseguir una reseña nueva este mes',
      profile: 'Completar los datos de la ficha',
      photos: 'Tener {target} fotos o más',
    },
    leverGain: '+{gain}',
    leverScore: 'te deja en {score}',
    checklistTitle: 'Sin completar',
  },
  locked: {
    title: 'Te explicamos cómo mejorar exactamente tu posicionamiento',
    description: 'Lo de arriba lo ve cualquiera que te busque. Lo de aquí abajo solo sale conectando tu ficha: qué piden tus clientes, qué reseñas tienes sin responder y qué tocar esta semana.',
    cta: 'Ver mi plan de mejora',
    sampleLabel: 'Ejemplo',
    lovesTitle: 'Lo que más valoran',
    improvesTitle: 'Lo que te está costando clientes',
    loves: [
      {
        title: 'El trato del equipo',
        description: 'Aparece en 6 de cada 10 reseñas de 5 estrellas del último trimestre.',
      },
      {
        title: 'La relación calidad-precio',
        description: 'Lo mencionan sobre todo los clientes de mediodía entre semana.',
      },
      {
        title: 'La terraza',
        description: 'Es el motivo más repetido entre quienes te descubren por Google Maps.',
      },
    ],
    improves: [
      {
        title: 'Los tiempos de espera del fin de semana',
        description: 'Concentra el 70 % de tus reseñas de 3 estrellas o menos.',
      },
      {
        title: '14 reseñas sin responder',
        description: 'Google premia las fichas que contestan, y las 4 más recientes son negativas.',
      },
      {
        title: 'Sin fotos nuevas en 5 meses',
        description: 'Las fichas que suben fotos cada mes salen antes en las búsquedas de tu zona.',
      },
    ],
  },
  errors: {
    rate_limited: 'Has hecho muchas búsquedas seguidas. Espera un minuto.',
    budget_exhausted: 'El analizador está muy solicitado ahora mismo. Vuelve en un rato.',
    temporarily_unavailable: 'No podemos analizar en este momento. Vuelve en un momento.',
    provider_unavailable: 'No hemos podido leer tu ficha en Google. Inténtalo otra vez.',
    not_found: 'Google no encuentra esa ficha.',
    session_expired: 'La búsqueda ha caducado. Búscalo otra vez.',
    session_exhausted: 'Ya has visto varios análisis. Haz una búsqueda nueva.',
    generic: 'Algo ha fallado. Inténtalo otra vez.',
  },
};

const en = {
  kicker: 'Free, in 10 seconds',
  title: 'See what Google shows about your business',
  subtitle: 'Search for your listing and we will tell you how it looks today. No sign-up, nothing to connect.',
  search: {
    label: 'Your business',
    placeholder: 'Your business name and city',
    button: 'Analyse for free',
    minChars: 'Type at least 3 letters',
    searching: 'Searching…',
    noResults: 'We could not find it. Try adding the city.',
    pickTitle: 'Pick your business',
    again: 'Search another',
  },
  report: {
    loading: 'Analysing your listing…',
    scoreTitle: 'Your listing right now',
    outOf: 'out of 100',
    labels: {
      excellent: 'Excellent',
      good: 'Good',
      fair: 'Needs work',
      critical: 'Critical',
    },
    blocks: {
      reviews_volume: 'Number of reviews',
      reviews_rating: 'Average rating',
      reviews_pace: 'Latest review',
      profile: 'Listing details',
      photos: 'Photos',
    },
    unknown: 'Google does not publish it',
    today: 'today',
    daysAgo: '{n} days ago',
    reviewsCount: '{n} reviews',
    noRating: 'no rating yet',
    photosCount: '{n} photos',
    photosCapped: '10 photos or more',
    noPhotos: 'none',
    profileComplete: 'complete',
    profileMissing: 'missing {n}',
    profileItems: {
      phone: 'phone',
      website: 'website',
      hours: 'opening hours',
      category: 'category',
    },
    warnings: {
      closed_temporarily: 'Google shows you as “temporarily closed”.',
      closed_permanently: 'Google shows you as “permanently closed”.',
    },
    source: 'Public Google data. We have not touched your listing.',
    mapsLink: 'See on Google Maps',
    checklist: {
      website: 'Website',
      phone: 'Phone',
      hours: 'Opening hours',
      category: 'Category',
      photos: '10+ photos',
      reviews_fresh: 'Recent reviews',
    },
    checklistUnknown: 'No data',
    highlights: {
      reviews_low_recent: '{count} of your last {of} reviews are 3★ or lower',
      reviews_foreign_language: '{count} of your last {of} reviews are in {language}',
      hours_days: 'You have opening hours set for {days} days a week',
    },
    languages: {
      en: 'English',
      fr: 'French',
      de: 'German',
      it: 'Italian',
      pt: 'Portuguese',
      es: 'Spanish',
      nl: 'Dutch',
      ru: 'Russian',
    },
    benchmarkTitle: 'What we see across the listings we manage',
    benchmarks: {
      website: 'Those with a website get {n} % more impressions on Google than those without.',
      photos: 'Those with 10 or more photos get {n} % more impressions than those with fewer.',
    },
    benchmarkSample: 'Measured across {n} real listings, comparing businesses of similar size. It is what we observe, not what we promise you.',
    points: '{pts} of {max} points',
    gapTitle: 'Where you are losing points',
    gapLine: 'You are {total} points short.',
    gapReviews: '{reviews} of them are in your reviews.',
    leversTitle: 'What would raise it most',
    levers: {
      reviews_volume: 'Reach {target} reviews',
      reviews_rating: 'Raise your average to {target}',
      reviews_pace: 'Get a new review this month',
      profile: 'Complete your listing details',
      photos: 'Have {target} photos or more',
    },
    leverGain: '+{gain}',
    leverScore: 'takes you to {score}',
    checklistTitle: 'Not done yet',
  },
  locked: {
    title: 'We show you exactly how to improve your ranking',
    description: 'Anyone searching for you sees what is above. What is below only comes from connecting your listing: what your customers ask for, which reviews you have left unanswered and what to fix this week.',
    cta: 'See my improvement plan',
    sampleLabel: 'Example',
    lovesTitle: 'What they value most',
    improvesTitle: 'What is costing you customers',
    loves: [
      {
        title: 'How the team treats people',
        description: 'It shows up in 6 out of 10 five-star reviews from the last quarter.',
      },
      {
        title: 'Value for money',
        description: 'Mentioned above all by weekday lunch customers.',
      },
      {
        title: 'The terrace',
        description: 'The most repeated reason among people who find you on Google Maps.',
      },
    ],
    improves: [
      {
        title: 'Weekend waiting times',
        description: 'They account for 70% of your reviews of 3 stars or fewer.',
      },
      {
        title: '14 unanswered reviews',
        description: 'Google rewards listings that reply, and the 4 most recent ones are negative.',
      },
      {
        title: 'No new photos in 5 months',
        description: 'Listings that post photos every month rank higher in local searches.',
      },
    ],
  },
  errors: {
    rate_limited: 'That is a lot of searches in a row. Wait a minute.',
    budget_exhausted: 'The analyser is very busy right now. Come back in a while.',
    temporarily_unavailable: 'We cannot analyse right now. Try again in a moment.',
    provider_unavailable: 'We could not read your listing on Google. Try again.',
    not_found: 'Google cannot find that listing.',
    session_expired: 'The search expired. Look it up again.',
    session_exhausted: 'You have already seen several analyses. Start a new search.',
    generic: 'Something went wrong. Try again.',
  },
};

const pt = {
  kicker: 'Grátis, em 10 segundos',
  title: 'Veja o que o Google mostra sobre o seu negócio',
  subtitle: 'Procure a sua ficha e dizemos-lhe como está hoje. Sem registo e sem ligar nada.',
  search: {
    label: 'O seu negócio',
    placeholder: 'Nome do seu negócio e cidade',
    button: 'Analisar grátis',
    minChars: 'Escreva pelo menos 3 letras',
    searching: 'A procurar…',
    noResults: 'Não encontrámos. Experimente juntar a cidade.',
    pickTitle: 'Escolha o seu negócio',
    again: 'Procurar outro',
  },
  report: {
    loading: 'A analisar a sua ficha…',
    scoreTitle: 'A sua ficha neste momento',
    outOf: 'em 100',
    labels: {
      excellent: 'Excelente',
      good: 'Bom',
      fair: 'A melhorar',
      critical: 'Crítico',
    },
    blocks: {
      reviews_volume: 'Número de avaliações',
      reviews_rating: 'Nota média',
      reviews_pace: 'Última avaliação',
      profile: 'Dados da ficha',
      photos: 'Fotografias',
    },
    unknown: 'O Google não publica',
    today: 'hoje',
    daysAgo: 'há {n} dias',
    reviewsCount: '{n} avaliações',
    noRating: 'ainda sem nota',
    photosCount: '{n} fotografias',
    photosCapped: '10 fotografias ou mais',
    noPhotos: 'nenhuma',
    profileComplete: 'completos',
    profileMissing: 'falta {n}',
    profileItems: {
      phone: 'telefone',
      website: 'site',
      hours: 'horário',
      category: 'categoria',
    },
    warnings: {
      closed_temporarily: 'O Google mostra-o como «temporariamente encerrado».',
      closed_permanently: 'O Google mostra-o como «permanentemente encerrado».',
    },
    source: 'Dados públicos do Google. Não mexemos na sua ficha.',
    mapsLink: 'Ver no Google Maps',
    checklist: {
      website: 'Site',
      phone: 'Telefone',
      hours: 'Horário',
      category: 'Categoria',
      photos: '10+ fotografias',
      reviews_fresh: 'Avaliações recentes',
    },
    checklistUnknown: 'Sem dados',
    highlights: {
      reviews_low_recent: '{count} das suas últimas {of} avaliações são de 3★ ou menos',
      reviews_foreign_language: '{count} das suas últimas {of} avaliações estão em {language}',
      hours_days: 'Tem horário definido {days} dias por semana',
    },
    languages: {
      en: 'inglês',
      fr: 'francês',
      de: 'alemão',
      it: 'italiano',
      pt: 'português',
      es: 'espanhol',
      nl: 'neerlandês',
      ru: 'russo',
    },
    benchmarkTitle: 'O que vemos nas fichas que gerimos',
    benchmarks: {
      website: 'As que têm site recebem {n} % mais impressões no Google do que as que não têm.',
      photos: 'As que têm 10 ou mais fotografias recebem {n} % mais impressões do que as que têm menos.',
    },
    benchmarkSample: 'Medido em {n} fichas reais, comparando negócios de dimensão parecida. É o que observamos, não o que lhe prometemos.',
    points: '{pts} de {max} pontos',
    gapTitle: 'Onde está a perder pontos',
    gapLine: 'Faltam-lhe {total} pontos.',
    gapReviews: '{reviews} estão nas suas avaliações.',
    leversTitle: 'O que mais lhe subiria',
    levers: {
      reviews_volume: 'Chegar a {target} avaliações',
      reviews_rating: 'Subir a nota média para {target}',
      reviews_pace: 'Conseguir uma avaliação nova este mês',
      profile: 'Completar os dados da ficha',
      photos: 'Ter {target} fotografias ou mais',
    },
    leverGain: '+{gain}',
    leverScore: 'deixa-o em {score}',
    checklistTitle: 'Por completar',
  },
  locked: {
    title: 'Explicamos-lhe exactamente como melhorar o seu posicionamento',
    description: 'O que está acima vê-o qualquer pessoa que o procure. O que está aqui em baixo só sai ligando a sua ficha: o que os seus clientes pedem, que avaliações tem por responder e o que mexer esta semana.',
    cta: 'Ver o meu plano de melhoria',
    sampleLabel: 'Exemplo',
    lovesTitle: 'O que mais valorizam',
    improvesTitle: 'O que lhe está a custar clientes',
    loves: [
      {
        title: 'O trato da equipa',
        description: 'Aparece em 6 de cada 10 avaliações de 5 estrelas do último trimestre.',
      },
      {
        title: 'A relação qualidade-preço',
        description: 'É mencionada sobretudo pelos clientes do almoço durante a semana.',
      },
      {
        title: 'A esplanada',
        description: 'É o motivo mais repetido entre quem o descobre no Google Maps.',
      },
    ],
    improves: [
      {
        title: 'Os tempos de espera ao fim de semana',
        description: 'Concentram 70 % das suas avaliações de 3 estrelas ou menos.',
      },
      {
        title: '14 avaliações por responder',
        description: 'O Google premeia as fichas que respondem, e as 4 mais recentes são negativas.',
      },
      {
        title: 'Sem fotografias novas há 5 meses',
        description: 'As fichas que publicam fotografias todos os meses aparecem antes nas pesquisas da sua zona.',
      },
    ],
  },
  errors: {
    rate_limited: 'Fez muitas pesquisas seguidas. Espere um minuto.',
    budget_exhausted: 'O analisador está muito solicitado agora. Volte daqui a pouco.',
    temporarily_unavailable: 'Não conseguimos analisar neste momento. Volte já a seguir.',
    provider_unavailable: 'Não conseguimos ler a sua ficha no Google. Tente outra vez.',
    not_found: 'O Google não encontra essa ficha.',
    session_expired: 'A pesquisa expirou. Procure outra vez.',
    session_exhausted: 'Já viu várias análises. Faça uma pesquisa nova.',
    generic: 'Algo falhou. Tente outra vez.',
  },
};

const de = {
  kicker: 'Kostenlos, in 10 Sekunden',
  title: 'Sehen Sie, was Google über Ihr Unternehmen zeigt',
  subtitle: 'Suchen Sie Ihren Eintrag und wir sagen Ihnen, wie er heute dasteht. Ohne Anmeldung, ohne etwas zu verbinden.',
  search: {
    label: 'Ihr Unternehmen',
    placeholder: 'Name und Stadt Ihres Unternehmens',
    button: 'Kostenlos analysieren',
    minChars: 'Mindestens 3 Buchstaben eingeben',
    searching: 'Wird gesucht…',
    noResults: 'Wir haben ihn nicht gefunden. Versuchen Sie es mit der Stadt.',
    pickTitle: 'Wählen Sie Ihr Unternehmen',
    again: 'Anderes suchen',
  },
  report: {
    loading: 'Ihr Eintrag wird analysiert…',
    scoreTitle: 'Ihr Eintrag im Moment',
    outOf: 'von 100',
    labels: {
      excellent: 'Ausgezeichnet',
      good: 'Gut',
      fair: 'Ausbaufähig',
      critical: 'Kritisch',
    },
    blocks: {
      reviews_volume: 'Anzahl der Bewertungen',
      reviews_rating: 'Durchschnittsnote',
      reviews_pace: 'Letzte Bewertung',
      profile: 'Angaben im Eintrag',
      photos: 'Fotos',
    },
    unknown: 'Google veröffentlicht es nicht',
    today: 'heute',
    daysAgo: 'vor {n} Tagen',
    reviewsCount: '{n} Bewertungen',
    noRating: 'noch ohne Note',
    photosCount: '{n} Fotos',
    photosCapped: '10 Fotos oder mehr',
    noPhotos: 'keine',
    profileComplete: 'vollständig',
    profileMissing: 'es fehlt {n}',
    profileItems: {
      phone: 'Telefon',
      website: 'Website',
      hours: 'Öffnungszeiten',
      category: 'Kategorie',
    },
    warnings: {
      closed_temporarily: 'Google zeigt Sie als „vorübergehend geschlossen“ an.',
      closed_permanently: 'Google zeigt Sie als „dauerhaft geschlossen“ an.',
    },
    source: 'Öffentliche Google-Daten. Wir haben Ihren Eintrag nicht angefasst.',
    mapsLink: 'Auf Google Maps ansehen',
    checklist: {
      website: 'Website',
      phone: 'Telefon',
      hours: 'Öffnungszeiten',
      category: 'Kategorie',
      photos: '10+ Fotos',
      reviews_fresh: 'Aktuelle Bewertungen',
    },
    checklistUnknown: 'Keine Daten',
    highlights: {
      reviews_low_recent: '{count} Ihrer letzten {of} Bewertungen haben 3★ oder weniger',
      reviews_foreign_language: '{count} Ihrer letzten {of} Bewertungen sind auf {language}',
      hours_days: 'Sie haben an {days} Tagen pro Woche Öffnungszeiten hinterlegt',
    },
    languages: {
      en: 'Englisch',
      fr: 'Französisch',
      de: 'Deutsch',
      it: 'Italienisch',
      pt: 'Portugiesisch',
      es: 'Spanisch',
      nl: 'Niederländisch',
      ru: 'Russisch',
    },
    benchmarkTitle: 'Was wir bei den Einträgen sehen, die wir betreuen',
    benchmarks: {
      website: 'Die mit Website erhalten {n} % mehr Impressionen bei Google als die ohne.',
      photos: 'Die mit 10 oder mehr Fotos erhalten {n} % mehr Impressionen als die mit weniger.',
    },
    benchmarkSample: 'Gemessen an {n} echten Einträgen, verglichen mit Unternehmen ähnlicher Größe. Das ist, was wir beobachten, nicht was wir Ihnen versprechen.',
    points: '{pts} von {max} Punkten',
    gapTitle: 'Wo Sie Punkte verlieren',
    gapLine: 'Ihnen fehlen {total} Punkte.',
    gapReviews: 'Davon liegen {reviews} in Ihren Bewertungen.',
    leversTitle: 'Was am meisten bringt',
    levers: {
      reviews_volume: '{target} Bewertungen erreichen',
      reviews_rating: 'Durchschnitt auf {target} anheben',
      reviews_pace: 'Diesen Monat eine neue Bewertung bekommen',
      profile: 'Die Angaben im Eintrag vervollständigen',
      photos: '{target} Fotos oder mehr haben',
    },
    leverGain: '+{gain}',
    leverScore: 'bringt Sie auf {score}',
    checklistTitle: 'Noch offen',
  },
  locked: {
    title: 'Wir erklären Ihnen genau, wie Sie Ihr Ranking verbessern',
    description: 'Was oben steht, sieht jeder, der Sie sucht. Was hier unten steht, gibt es nur, wenn Sie Ihren Eintrag verbinden: was Ihre Kunden verlangen, welche Bewertungen unbeantwortet sind und was diese Woche zu tun ist.',
    cta: 'Meinen Verbesserungsplan ansehen',
    sampleLabel: 'Beispiel',
    lovesTitle: 'Was am meisten geschätzt wird',
    improvesTitle: 'Was Sie Kunden kostet',
    loves: [
      {
        title: 'Der Umgang des Teams',
        description: 'Kommt in 6 von 10 Fünf-Sterne-Bewertungen des letzten Quartals vor.',
      },
      {
        title: 'Das Preis-Leistungs-Verhältnis',
        description: 'Wird vor allem von Mittagsgästen unter der Woche erwähnt.',
      },
      {
        title: 'Die Terrasse',
        description: 'Der häufigste Grund bei allen, die Sie über Google Maps entdecken.',
      },
    ],
    improves: [
      {
        title: 'Die Wartezeiten am Wochenende',
        description: 'Auf sie entfallen 70 % Ihrer Bewertungen mit 3 Sternen oder weniger.',
      },
      {
        title: '14 unbeantwortete Bewertungen',
        description: 'Google belohnt Einträge, die antworten, und die 4 jüngsten sind negativ.',
      },
      {
        title: 'Seit 5 Monaten keine neuen Fotos',
        description: 'Einträge mit monatlichen Fotos erscheinen in Ihrer Gegend weiter oben.',
      },
    ],
  },
  errors: {
    rate_limited: 'Das waren viele Suchen hintereinander. Warten Sie eine Minute.',
    budget_exhausted: 'Die Analyse ist gerade stark gefragt. Kommen Sie später wieder.',
    temporarily_unavailable: 'Wir können gerade nicht analysieren. Versuchen Sie es gleich noch einmal.',
    provider_unavailable: 'Wir konnten Ihren Eintrag bei Google nicht lesen. Versuchen Sie es erneut.',
    not_found: 'Google findet diesen Eintrag nicht.',
    session_expired: 'Die Suche ist abgelaufen. Suchen Sie noch einmal.',
    session_exhausted: 'Sie haben schon mehrere Analysen gesehen. Starten Sie eine neue Suche.',
    generic: 'Etwas ist schiefgelaufen. Versuchen Sie es erneut.',
  },
};

const fr = {
  kicker: 'Gratuit, en 10 secondes',
  title: 'Voyez ce que Google montre de votre établissement',
  subtitle: 'Cherchez votre fiche et nous vous disons où elle en est aujourd’hui. Sans inscription et sans rien connecter.',
  search: {
    label: 'Votre établissement',
    placeholder: 'Nom de votre établissement et ville',
    button: 'Analyser gratuitement',
    minChars: 'Saisissez au moins 3 lettres',
    searching: 'Recherche…',
    noResults: 'Nous ne l’avons pas trouvé. Essayez d’ajouter la ville.',
    pickTitle: 'Choisissez votre établissement',
    again: 'En chercher un autre',
  },
  report: {
    loading: 'Analyse de votre fiche…',
    scoreTitle: 'Votre fiche en ce moment',
    outOf: 'sur 100',
    labels: {
      excellent: 'Excellent',
      good: 'Bien',
      fair: 'À améliorer',
      critical: 'Critique',
    },
    blocks: {
      reviews_volume: 'Nombre d’avis',
      reviews_rating: 'Note moyenne',
      reviews_pace: 'Dernier avis',
      profile: 'Informations de la fiche',
      photos: 'Photos',
    },
    unknown: 'Google ne le publie pas',
    today: 'aujourd’hui',
    daysAgo: 'il y a {n} jours',
    reviewsCount: '{n} avis',
    noRating: 'pas encore de note',
    photosCount: '{n} photos',
    photosCapped: '10 photos ou plus',
    noPhotos: 'aucune',
    profileComplete: 'complètes',
    profileMissing: 'il manque {n}',
    profileItems: {
      phone: 'téléphone',
      website: 'site web',
      hours: 'horaires',
      category: 'catégorie',
    },
    warnings: {
      closed_temporarily: 'Google vous affiche comme « fermé temporairement ».',
      closed_permanently: 'Google vous affiche comme « définitivement fermé ».',
    },
    source: 'Données publiques de Google. Nous n’avons pas touché à votre fiche.',
    mapsLink: 'Voir sur Google Maps',
    checklist: {
      website: 'Site web',
      phone: 'Téléphone',
      hours: 'Horaires',
      category: 'Catégorie',
      photos: '10+ photos',
      reviews_fresh: 'Avis récents',
    },
    checklistUnknown: 'Pas de données',
    highlights: {
      reviews_low_recent: '{count} de vos {of} derniers avis sont à 3★ ou moins',
      reviews_foreign_language: '{count} de vos {of} derniers avis sont en {language}',
      hours_days: 'Vous avez des horaires renseignés {days} jours par semaine',
    },
    languages: {
      en: 'anglais',
      fr: 'français',
      de: 'allemand',
      it: 'italien',
      pt: 'portugais',
      es: 'espagnol',
      nl: 'néerlandais',
      ru: 'russe',
    },
    benchmarkTitle: 'Ce que nous voyons sur les fiches que nous gérons',
    benchmarks: {
      website: 'Celles qui ont un site reçoivent {n} % d’impressions en plus sur Google que celles qui n’en ont pas.',
      photos: 'Celles qui ont 10 photos ou plus reçoivent {n} % d’impressions en plus que celles qui en ont moins.',
    },
    benchmarkSample: 'Mesuré sur {n} fiches réelles, en comparant des établissements de taille comparable. C’est ce que nous observons, pas ce que nous vous promettons.',
    points: '{pts} points sur {max}',
    gapTitle: 'Où vous perdez des points',
    gapLine: 'Il vous manque {total} points.',
    gapReviews: '{reviews} sont dans vos avis.',
    leversTitle: 'Ce qui ferait le plus monter',
    levers: {
      reviews_volume: 'Atteindre {target} avis',
      reviews_rating: 'Monter la note moyenne à {target}',
      reviews_pace: 'Obtenir un nouvel avis ce mois-ci',
      profile: 'Compléter les informations de la fiche',
      photos: 'Avoir {target} photos ou plus',
    },
    leverGain: '+{gain}',
    leverScore: 'vous amène à {score}',
    checklistTitle: 'À compléter',
  },
  locked: {
    title: 'Nous vous expliquons exactement comment améliorer votre positionnement',
    description: 'Ce qui est au-dessus, tout le monde le voit en vous cherchant. Ce qui est ici en dessous ne sort qu’en connectant votre fiche : ce que vos clients demandent, les avis restés sans réponse et ce qu’il faut corriger cette semaine.',
    cta: 'Voir mon plan d’amélioration',
    sampleLabel: 'Exemple',
    lovesTitle: 'Ce qu’ils apprécient le plus',
    improvesTitle: 'Ce qui vous coûte des clients',
    loves: [
      {
        title: 'L’accueil de l’équipe',
        description: 'Cité dans 6 avis 5 étoiles sur 10 du dernier trimestre.',
      },
      {
        title: 'Le rapport qualité-prix',
        description: 'Mentionné surtout par la clientèle du déjeuner en semaine.',
      },
      {
        title: 'La terrasse',
        description: 'La raison la plus répétée chez ceux qui vous découvrent sur Google Maps.',
      },
    ],
    improves: [
      {
        title: 'Les temps d’attente du week-end',
        description: 'Ils concentrent 70 % de vos avis de 3 étoiles ou moins.',
      },
      {
        title: '14 avis sans réponse',
        description: 'Google favorise les fiches qui répondent, et les 4 plus récents sont négatifs.',
      },
      {
        title: 'Aucune photo nouvelle depuis 5 mois',
        description: 'Les fiches qui publient des photos chaque mois sortent plus haut dans votre secteur.',
      },
    ],
  },
  errors: {
    rate_limited: 'Cela fait beaucoup de recherches d’affilée. Attendez une minute.',
    budget_exhausted: 'L’analyseur est très sollicité en ce moment. Revenez un peu plus tard.',
    temporarily_unavailable: 'Nous ne pouvons pas analyser pour l’instant. Réessayez dans un moment.',
    provider_unavailable: 'Nous n’avons pas pu lire votre fiche sur Google. Réessayez.',
    not_found: 'Google ne trouve pas cette fiche.',
    session_expired: 'La recherche a expiré. Cherchez à nouveau.',
    session_exhausted: 'Vous avez déjà vu plusieurs analyses. Lancez une nouvelle recherche.',
    generic: 'Quelque chose a échoué. Réessayez.',
  },
};

export const auditorContent = { es, en, pt, de, fr };

/** El copy del auditor en el idioma indicado, con fallback a español. */
export function pickAuditorContent(language) {
  return auditorContent[language] || auditorContent.es;
}
